import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Collapse,
  Empty,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { ProjectPhaseStatus } from "../../enum/common";
import {
  IProductionProjectPhaseMilestone,
  IProductionProjectPhases,
} from "../../models/Production";
import {
  useCreateProductionProjectPhaseMilestone,
  useDeleteProductionProjectPhases,
  useGetProductionProjectByAlias,
  useGetProductionProjectPhases,
} from "../../query/ProductionQuery";
import { notify } from "../../utils/toast";
import ModalAddProductionProjectPhase from "../modal/ModalAddProductionProjectPhase";
import ModalEditProductionProjectPhase from "../modal/ModalEditProductionProjectPhase";
import ModalAddProductionProjectPhaseMilestone from "../modal/ModalAddProductionProjectPhaseMilestone";

const { Panel } = Collapse;

interface IProductionProjectPhaseProps {
  alias: string;
}

const ProductionProjectPhaseTab = ({ alias }: IProductionProjectPhaseProps) => {
  const { data: project } = useGetProductionProjectByAlias(alias);
  const { data: phases, isLoading } = useGetProductionProjectPhases(
    project?.id,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isAddPhaseModalOpen, setIsAddPhaseModalOpen] =
    useState<boolean>(false);
  const [isAddMilestoneModalOpen, setIsAddMilestoneModalOpen] =
    useState<boolean>(false);
  const [selectedPhase, setSelectedPhase] =
    useState<IProductionProjectPhases | null>(null);
  const [selectedPhaseForMilestone, setSelectedPhaseForMilestone] = useState<
    number | null
  >(null);
  const createMilestoneMutation = useCreateProductionProjectPhaseMilestone(
    project?.id!,
  );
  const deleteProjectPhase = useDeleteProductionProjectPhases();

  const handleEditPhase = (phase: IProductionProjectPhases) => {
    setSelectedPhase(phase);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedPhase(null);
  };

  const handleOpenAddPhase = () => {
    setIsAddPhaseModalOpen(true);
  };

  const handleCloseAddPhase = () => {
    setIsAddPhaseModalOpen(false);
  };

  const handleOpenAddMilestone = (phaseId: number) => {
    setSelectedPhaseForMilestone(phaseId);
    setIsAddMilestoneModalOpen(true);
  };

  const handleCloseAddMilestone = () => {
    setIsAddMilestoneModalOpen(false);
    setSelectedPhaseForMilestone(null);
  };

  const handleSubmitMilestone = async (
    milestone: Omit<
      IProductionProjectPhaseMilestone,
      "id" | "prdProjectPhaseId"
    >,
  ) => {
    if (!selectedPhaseForMilestone) return;

    try {
      await createMilestoneMutation.mutateAsync({
        ...milestone,
        prdProjectPhaseId: selectedPhaseForMilestone,
      });
      notify.success("Thêm cột mốc thành công");
    } catch (error) {
      notify.error("Thêm cột mốc thất bại");
    }
  };

  const getStatusTag = (status: ProjectPhaseStatus) => {
    const statusConfig: Record<
      ProjectPhaseStatus,
      { color: string; text: string; icon: React.ReactNode }
    > = {
      [ProjectPhaseStatus.COMPLETED]: {
        color: "success",
        text: "Hoàn thành",
        icon: <CheckCircleOutlined />,
      },
      [ProjectPhaseStatus.IN_PROGRESS]: {
        color: "processing",
        text: "Đang thực hiện",
        icon: <CheckCircleOutlined />,
      },
      [ProjectPhaseStatus.PENDING]: {
        color: "default",
        text: "Chờ thực hiện",
        icon: <CheckCircleOutlined />,
      },
      [ProjectPhaseStatus.DELAYED]: {
        color: "warning",
        text: "Trễ hạn",
        icon: <CheckCircleOutlined />,
      },
    };

    const config = statusConfig[status];
    return (
      <Tag
        color={config.color}
        icon={config.icon}
        style={{
          borderRadius: "16px",
          fontSize: "12px",
          padding: "2px 12px",
          fontWeight: 500,
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const getTimelineColor = (status: ProjectPhaseStatus) => {
    switch (status) {
      case ProjectPhaseStatus.COMPLETED:
        return "#52c41a";
      case ProjectPhaseStatus.IN_PROGRESS:
        return "#1890ff";
      case ProjectPhaseStatus.DELAYED:
        return "#faad14";
      default:
        return "#dbd5d5";
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!phases || phases.length === 0) {
    return (
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAddPhase}
          >
            Thêm giai đoạn
          </Button>
        </div>
        <Empty
          description="Chưa có giai đoạn nào"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <ModalAddProductionProjectPhase
          open={isAddPhaseModalOpen}
          onCancel={handleCloseAddPhase}
          prdProjectId={project?.id || 0}
        />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenAddPhase}
        >
          Thêm giai đoạn
        </Button>
      </div>
      <ModalEditProductionProjectPhase
        open={isEditModalOpen}
        onCancel={handleCloseModal}
        phase={selectedPhase}
      />
      <ModalAddProductionProjectPhase
        open={isAddPhaseModalOpen}
        onCancel={handleCloseAddPhase}
        prdProjectId={project?.id || 0}
      />
      <ModalAddProductionProjectPhaseMilestone
        open={isAddMilestoneModalOpen}
        onCancel={handleCloseAddMilestone}
        onSubmit={handleSubmitMilestone}
        phaseId={selectedPhaseForMilestone || 0}
      />
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            left: "12px",
            top: "12px",
            bottom: "0",
            width: "3px",
            zIndex: 0,
            background: "#ebcaca",
          }}
        />

        {phases.map((phase, index) => (
          <div
            key={phase.id}
            style={{
              position: "relative",
              marginBottom: index === phases.length - 1 ? 0 : "20px",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "16px",
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                background: getTimelineColor(phase.status),
                boxShadow: `0 0 0 2px ${getTimelineColor(phase.status)}`,
                zIndex: 1,
              }}
            />

            <Card
              style={{
                marginLeft: "50px",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "4px",
                  }}
                >
                  <Space direction="vertical" size={6}>
                    <Space size={12}>
                      <Typography.Text
                        style={{ fontSize: 18, fontWeight: 600 }}
                      >
                        Giai đoạn {phase.phaseOrder}: {phase.phaseName}
                      </Typography.Text>
                      {getStatusTag(phase.status)}
                    </Space>
                  </Space>

                  <Space size={8}>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      size="middle"
                      onClick={() => handleEditPhase(phase)}
                    />
                    <Popconfirm
                      title="Xóa giai đoạn"
                      description="Bạn có chắc chắn muốn xóa giai đoạn này?"
                      onConfirm={async () => {
                        try {
                          await deleteProjectPhase.mutateAsync(phase.id);
                          notify.success("Xóa giai đoạn thành công");
                        } catch (error) {
                          notify.error("Xóa giai đoạn thất bại");
                        }
                      }}
                      okText="Có"
                      cancelText="Không"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="middle"
                        loading={deleteProjectPhase.isPending}
                        danger
                      />
                    </Popconfirm>
                  </Space>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    borderRadius: "8px",
                  }}
                >
                  <CalendarOutlined style={{ fontSize: "16px" }} />
                  <Typography.Text style={{ fontWeight: 500 }}>
                    {dayjs(phase.startDate).format("YYYY-MM-DD")} —{" "}
                    {dayjs(phase.endDate).format("YYYY-MM-DD")}
                  </Typography.Text>
                </div>
                <div>
                  <Progress
                    percent={phase.progress}
                    strokeColor={{
                      "0%": getTimelineColor(phase.status),
                      "100%": getTimelineColor(phase.status),
                    }}
                    trailColor={"#ded6d5"}
                    className="mb-2"
                  />
                </div>

                {phase.prdProjectPhaseMilestones && (
                  <div>
                    <Collapse
                      expandIconPosition="end"
                      defaultActiveKey={[phase.id]}
                    >
                      <Panel
                        key={phase.id}
                        header={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Typography.Text
                              style={{
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                            >
                              Cột mốc quan trọng
                            </Typography.Text>
                          </div>
                        }
                        extra={
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="small"
                            style={{
                              color: "#1890ff",
                              fontSize: "13px",
                              fontWeight: 500,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAddMilestone(phase.id);
                            }}
                          >
                            Thêm cột mốc
                          </Button>
                        }
                        style={{
                          borderBottom: "none",
                        }}
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size={12}
                        >
                          {phase.prdProjectPhaseMilestones.map((milestone) => (
                            <div
                              key={milestone.id}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography.Text
                                style={{
                                  textDecoration: milestone.isCompleted
                                    ? "line-through"
                                    : "none",
                                  fontWeight: milestone.isCompleted ? 400 : 500,
                                }}
                              >
                                {milestone.isCompleted ? (
                                  <CheckCircleOutlined
                                    style={{
                                      color: "#52c41a",
                                      marginRight: 4,
                                    }}
                                  />
                                ) : (
                                  <CheckCircleOutlined
                                    style={{
                                      marginRight: 4,
                                    }}
                                  />
                                )}

                                {milestone.milestoneName}
                              </Typography.Text>

                              <Space>
                                <Typography.Text
                                  style={{
                                    fontWeight: 500,
                                  }}
                                >
                                  {milestone.completedDate
                                    ? dayjs(milestone.completedDate).format(
                                        "DD-MM-YYYY",
                                      )
                                    : "Hạn " +
                                      dayjs(milestone.targetDate).format(
                                        "DD-MM-YYYY",
                                      )}
                                </Typography.Text>
                              </Space>
                            </div>
                          ))}
                        </Space>
                      </Panel>
                    </Collapse>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductionProjectPhaseTab;
